/* ==========================================================================
   Theme Components — Wishlist (Favoritos)
   Web Components to manage wishlist list via localStorage.
   ========================================================================== */

const THEME_WISHLIST_KEY = 'theme-wishlist';

/**
 * LocalStorage utilities for wishlist
 */
const WishlistStore = {
  get() {
    try {
      return JSON.parse(localStorage.getItem(THEME_WISHLIST_KEY)) || [];
    } catch {
      return [];
    }
  },

  set(items) {
    localStorage.setItem(THEME_WISHLIST_KEY, JSON.stringify(items));
    document.dispatchEvent(new CustomEvent('theme:wishlist:change', { detail: { items } }));
  },

  has(handle) {
    return this.get().includes(handle);
  },

  add(handle) {
    const items = this.get();
    if (!items.includes(handle)) {
      items.push(handle);
      this.set(items);
    }
    return items;
  },

  remove(handle) {
    const items = this.get().filter(h => h !== handle);
    this.set(items);
    return items;
  },

  toggle(handle) {
    return this.has(handle) ? this.remove(handle) : this.add(handle);
  },

  count() {
    return this.get().length;
  }
};

/* ----------------------------------------------------------------
   <wishlist-toggle> — Wishlist toggle button
   ---------------------------------------------------------------- */
if (!customElements.get('wishlist-toggle')) {
  class WishlistToggle extends HTMLElement {
    connectedCallback() {
      this.handle = this.dataset.productHandle;
      if (!this.handle) return;

      this.button = this.querySelector('.wishlist-toggle');
      if (!this.button) return;

      this._syncState();

      this.button.addEventListener('click', this._onClick.bind(this));
      document.addEventListener('theme:wishlist:change', this._onWishlistChange.bind(this));
    }

    disconnectedCallback() {
      document.removeEventListener('theme:wishlist:change', this._onWishlistChange.bind(this));
    }

    _onClick(e) {
      e.preventDefault();
      e.stopPropagation();

      const items = WishlistStore.toggle(this.handle);
      const isActive = items.includes(this.handle);

      // Animate
      this.button.classList.add('wishlist-toggle--animating');
      this.button.addEventListener('animationend', () => {
        this.button.classList.remove('wishlist-toggle--animating');
      }, { once: true });

      this.dispatchEvent(new CustomEvent('theme:wishlist:toggle', {
        bubbles: true,
        detail: { handle: this.handle, active: isActive }
      }));
    }

    _onWishlistChange() {
      this._syncState();
    }

    _syncState() {
      const isActive = WishlistStore.has(this.handle);
      this.button.setAttribute('aria-pressed', String(isActive));
    }
  }

  customElements.define('wishlist-toggle', WishlistToggle);
}

/* ----------------------------------------------------------------
   <wishlist-page> — Wishlist page grid
   ---------------------------------------------------------------- */
if (!customElements.get('wishlist-page')) {
  class WishlistPage extends HTMLElement {
    connectedCallback() {
      this.gridEl = this.querySelector('[data-wishlist-grid]');
      this.loadingEl = this.querySelector('[data-wishlist-loading]');
      this.emptyEl = this.querySelector('[data-wishlist-empty]');
      this.countEl = this.querySelector('[data-wishlist-count]');
      this.headerEl = this.querySelector('[data-wishlist-header]');

      this._moneyFormat = this.dataset.moneyFormat || '{{ amount }}';
      this._cardRatio = this.dataset.cardRatio || '1 / 1';

      this._loadWishlist();

      document.addEventListener('theme:wishlist:change', () => this._loadWishlist());
    }

    async _loadWishlist() {
      const handles = WishlistStore.get();

      if (handles.length === 0) {
        this._showEmpty();
        return;
      }

      this._showLoading();

      try {
        const products = await this._fetchProducts(handles);
        this._renderProducts(products, handles);
      } catch (err) {
        console.error('[wishlist] Error loading products:', err);
        this._showEmpty();
      }
    }

    async _fetchProducts(handles) {
      const products = [];

      const fetchPromises = handles.map(async (handle) => {
        try {
          const res = await fetch(`/products/${handle}.js`);
          if (res.ok) {
            const product = await res.json();
            products.push(product);
          }
        } catch {
          // Product might have been deleted/hidden
        }
      });

      await Promise.all(fetchPromises);
      return products;
    }

    _renderProducts(products, originalHandles) {
      if (products.length === 0) {
        this._showEmpty();
        return;
      }

      // Maintain original order of localStorage
      const sorted = originalHandles
        .map(h => products.find(p => p.handle === h))
        .filter(Boolean);

      const cards = sorted.map(product => this._renderCard(product)).join('');

      this.gridEl.innerHTML = cards;
      this._updateCount(sorted.length);
      this._show('grid');
    }

    _renderCard(product) {
      const variant = product.variants[0];
      const price = this._formatMoney(variant.price);
      const comparePrice = variant.compare_at_price && variant.compare_at_price > variant.price
        ? this._formatMoney(variant.compare_at_price)
        : '';

      const imgSrc = product.featured_image
        ? this._resizeImg(product.featured_image, '600x')
        : '';

      const tag = Array.isArray(product.tags) && product.tags.length > 0
        ? product.tags[0]
        : '';
      const tagHtml = tag
        ? `<p class="wishlist-card__tag">${this._escapeHtml(tag)}</p>`
        : '';

      const comparePriceHtml = comparePrice
        ? `<span class="wishlist-card__price--compare">${comparePrice}</span>`
        : '';

      const mediaHtml = imgSrc
        ? `<img src="${imgSrc}" alt="${this._escapeHtml(product.title)}" loading="lazy">`
        : `<div class="wishlist-card__placeholder">
            <svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path d="M2.5 2.5h15v15h-15z" fill="none" stroke="currentColor" stroke-width=".8"/>
              <circle cx="7" cy="7" r="1.5" fill="currentColor" opacity=".4"/>
              <path d="M2.5 13l4-4 3 3 2.5-2.5 5.5 5v3h-15z" fill="currentColor" opacity=".2"/>
            </svg>
          </div>`;

      const typeHtml = product.type
        ? `<p class="wishlist-card__type">${this._escapeHtml(product.type)}</p>`
        : '';

      return `
        <div class="wishlist-card">
          <div class="wishlist-card__media" style="--wishlist-card-ratio: ${this._cardRatio};">
            <a class="wishlist-card__media-link" href="/products/${product.handle}" aria-label="${this._escapeHtml(product.title)}">
              ${mediaHtml}
            </a>
            <wishlist-toggle data-product-handle="${product.handle}">
              <button type="button" class="wishlist-toggle" aria-pressed="true" aria-label="Remover dos favoritos">
                <span class="wishlist-toggle__icon">
                  <svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10 4.748C8.77 2.622 6.318 1.94 4.433 3.56c-1.886 1.62-2.15 4.326-.665 6.245C5.04 11.452 10 15.75 10 15.75s4.96-4.298 6.232-5.945c1.485-1.919 1.252-4.641-.665-6.245C13.682 1.924 11.23 2.622 10 4.748Z"/>
                  </svg>
                </span>
              </button>
            </wishlist-toggle>
          </div>
          <div class="wishlist-card__body">
            ${tagHtml}
            <h3 class="wishlist-card__title">
              <a href="/products/${product.handle}">${this._escapeHtml(product.title)}</a>
            </h3>
            ${typeHtml}
            <p class="wishlist-card__price">${comparePriceHtml}${price}</p>
          </div>
        </div>
      `;
    }

    _formatMoney(cents) {
      const amount = (cents / 100).toFixed(2).replace('.', ',');
      return this._moneyFormat
        .replace('{{amount}}', amount)
        .replace('{{ amount }}', amount)
        .replace('{{amount_with_comma_separator}}', amount)
        .replace('{{ amount_with_comma_separator }}', amount);
    }

    _resizeImg(url, size) {
      if (!url) return '';
      const match = url.match(/\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i);
      if (match) {
        return url.replace(match[0], `_${size}.${match[1]}${match[2] || ''}`);
      }
      return url;
    }

    _escapeHtml(str) {
      const div = document.createElement('div');
      div.textContent = str || '';
      return div.innerHTML;
    }

    _updateCount(count) {
      if (this.countEl) {
        this.countEl.textContent = `(${count})`;
      }
      if (this.headerEl) {
        this.headerEl.style.display = count > 0 ? '' : 'none';
      }
    }

    _showLoading() {
      if (this.loadingEl) this.loadingEl.style.display = '';
      if (this.gridEl) this.gridEl.style.display = 'none';
      if (this.emptyEl) this.emptyEl.style.display = 'none';
    }

    _showEmpty() {
      if (this.loadingEl) this.loadingEl.style.display = 'none';
      if (this.gridEl) this.gridEl.style.display = 'none';
      if (this.emptyEl) this.emptyEl.style.display = '';
      if (this.headerEl) this.headerEl.style.display = 'none';
    }

    _show(which) {
      if (this.loadingEl) this.loadingEl.style.display = 'none';
      if (this.gridEl) this.gridEl.style.display = '';
      if (this.emptyEl) this.emptyEl.style.display = 'none';
    }
  }

  customElements.define('wishlist-page', WishlistPage);
}

/* ----------------------------------------------------------------
   <wishlist-count> — Badge counter on header
   ---------------------------------------------------------------- */
if (!customElements.get('wishlist-count')) {
  class WishlistCount extends HTMLElement {
    connectedCallback() {
      this._countEl = this.querySelector('.header-actions__wishlist-count');
      this._sync();
      document.addEventListener('theme:wishlist:change', () => this._sync());
    }

    _sync() {
      const count = WishlistStore.count();
      if (count > 0) {
        this.hidden = false;
        if (this._countEl) this._countEl.textContent = count;
      } else {
        this.hidden = true;
      }
    }
  }

  customElements.define('wishlist-count', WishlistCount);
}
